import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiChevronLeft,
  FiSearch,
  FiDownload,
  FiLoader,
} from "react-icons/fi";

import toast from "react-hot-toast";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getAllAutoPoolPayment,
} from "../../store/slices/pool";

const AutoPoolPayment = () => {
  const dispatch = useDispatch();

  /* =====================================================
     REDUX
  ===================================================== */

  const {
    autoPoolPayments = [],
    autoPoolPaymentsLoading,
    autoPoolPaymentsError,
    autoPoolPaymentsPagination = {},
  } = useSelector((state) => state.pool);

  /* =====================================================
     STATE
  ===================================================== */

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] =
    useState(false);

  /* =====================================================
     FETCH AUTO POOL PAYMENT
  ===================================================== */

  useEffect(() => {
    dispatch(
      getAllAutoPoolPayment({
        page,
        limit,
        search: search.trim(),
      })
    );
  }, [dispatch, page, limit, search]);

  /* =====================================================
     PAGINATION
  ===================================================== */

  const totalRecords =
    Number(
      autoPoolPaymentsPagination?.totalRecords ||
      autoPoolPaymentsPagination?.total ||
      0
    );

  const currentPage =
    Number(
      autoPoolPaymentsPagination?.currentPage ||
      page
    );

  const currentLimit =
    Number(
      autoPoolPaymentsPagination?.limit ||
      limit
    );

  const totalPages =
    Number(
      autoPoolPaymentsPagination?.totalPages
    ) ||
    Math.ceil(
      totalRecords / currentLimit
    ) ||
    1;

  const hasNextPage =
    autoPoolPaymentsPagination?.hasNextPage ??
    currentPage < totalPages;

  const hasPreviousPage =
    autoPoolPaymentsPagination?.hasPreviousPage ??
    currentPage > 1;

  /* =====================================================
     FORMAT NUMBER
  ===================================================== */

  const formatNumber = (number) => {
    return Number(number || 0).toLocaleString(
      "en-IN"
    );
  };

  /* =====================================================
     FORMAT REWARD
  ===================================================== */

  const formatReward = (reward) => {
    return `$${Number(reward || 0).toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }
    )}`;
  };

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  /* =====================================================
     SEARCH
  ===================================================== */

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  /* =====================================================
     PER PAGE
  ===================================================== */

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  /* =====================================================
     PREVIOUS
  ===================================================== */

  const handlePrevious = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1);
    }
  };

  /* =====================================================
     NEXT
  ===================================================== */

  const handleNext = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  /* =====================================================
     PAGE CHANGE
  ===================================================== */

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages
    ) {
      setPage(pageNumber);
    }
  };

  /* =====================================================
     PAGE NUMBERS
  ===================================================== */

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from(
        { length: totalPages },
        (_, index) => index + 1
      );
    }

    if (currentPage <= 3) {
      return [
        1,
        2,
        3,
        4,
        "...",
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 2) {
      return [
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  /* =====================================================
     DOWNLOAD PDF
  ===================================================== */

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      /*
       * Fetch all records matching current search.
       *
       * Current API response has 220 records and
       * supports pagination, so a large limit is used
       * for the export.
       */
      const response = await dispatch(
        getAllAutoPoolPayment({
          page: 1,
          limit: 100000,
          search: search.trim(),
        })
      ).unwrap();

      /* =================================================
         GET RECORDS FROM RESPONSE
      ================================================= */

      const records = Array.isArray(
        response?.data?.records
      )
        ? response.data.records
        : Array.isArray(response?.records)
          ? response.records
          : Array.isArray(response)
            ? response
            : [];

      if (!records.length) {
        toast.error(
          "No auto pool payment data found"
        );
        return;
      }

      /* =================================================
         CREATE PDF
      ================================================= */

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(16);

      doc.text(
        "Auto Pool Payment",
        14,
        15
      );

      doc.setFontSize(9);

      doc.text(
        `Total Records: ${records.length}`,
        14,
        22
      );

      if (search.trim()) {
        doc.text(
          `Search: ${search.trim()}`,
          14,
          28
        );
      }

      /* =================================================
         PDF ROWS
      ================================================= */

      const rows = records.map(
        (item, index) => {
          const user = item?.userId || {};

          return [
            index + 1,
            user?.name || "-",
            user?.userId || "-",
            user?.email || "-",
            item?.remarks || "-",
            `Pool ${item?.triggerPool || "-"}`,
            `Pool ${item?.paidPool || "-"}`,
            formatReward(item?.reward),
            formatDate(item?.createdAt),
          ];
        }
      );

      /* =================================================
         PDF TABLE
      ================================================= */

      autoTable(doc, {
        startY: search.trim() ? 34 : 28,

        head: [
          [
            "#",
            "Name",
            "User ID",
            "Email",
            "Remarks",
            "Trigger Pool",
            "Paid Pool",
            "Reward",
            "Date",
          ],
        ],

        body: rows,

        styles: {
          fontSize: 8,
          cellPadding: 2,
        },

        headStyles: {
          fontSize: 8,
        },

        columnStyles: {
          0: {
            cellWidth: 10,
          },
          1: {
            cellWidth: 30,
          },
          2: {
            cellWidth: 25,
          },
          3: {
            cellWidth: 40,
          },
          4: {
            cellWidth: 55,
          },
          5: {
            cellWidth: 25,
          },
          6: {
            cellWidth: 25,
          },
          7: {
            cellWidth: 25,
          },
          8: {
            cellWidth: 30,
          },
        },
      });

      /* =================================================
         SAVE
      ================================================= */

      doc.save(
        `auto-pool-payment-${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`
      );

      toast.success(
        "PDF downloaded successfully"
      );
    } catch (error) {
      console.error(
        "Download PDF error:",
        error
      );

      toast.error(
        typeof error === "string"
          ? error
          : error?.message ||
          "Failed to download PDF"
      );
    } finally {
      setDownloadLoading(false);

      /*
       * Restore the current table page after
       * downloading all records.
       */
      dispatch(
        getAllAutoPoolPayment({
          page,
          limit,
          search: search.trim(),
        })
      );
    }
  };

  /* =====================================================
     STATUS CLASS
  ===================================================== */

  const getStatusClass = (remarks) => {
    const value = String(
      remarks || ""
    ).toLowerCase();

    if (
      value.includes("referral bonus")
    ) {
      return "completed";
    }

    return "filling";
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="gi-referral-page">

      {/* =====================================================
          BREADCRUMB
      ===================================================== */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />

        <span>
          Auto Pool
        </span>

      </div>

      {/* =====================================================
          TABLE CARD
      ===================================================== */}

      <section className="gi-referral-table-card">

        {/* =================================================
            FILTER
        ================================================= */}

        {/* ================= FILTER BAR ================= */}

        <div className="gi-users-filter">

          {/* PER PAGE */}

          <div className="gi-users-page-size me-2">

            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                const newLimit =
                  Number(e.target.value);

                setLimit(newLimit);
                setPage(1);
              }}
              disabled={autoPoolPaymentsLoading}
            >

              <option value={10}>
                10
              </option>

              <option value={25}>
                25
              </option>

              <option value={50}>
                50
              </option>

              <option value={100}>
                100
              </option>

            </select>

            <span>
              per page
            </span>

          </div>


          {/* SEARCH */}

          <div className="gi-users-search me-2">

            <FiSearch />

            <input
              type="text"
              placeholder="Search auto pool payment..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />

          </div>


          {/* DOWNLOAD */}

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              autoPoolPaymentsLoading ||
              autoPoolPayments.length === 0
            }
          >

            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>

          </button>

        </div>

        {/* =================================================
            TABLE
        ================================================= */}

        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  User
                </th>

                <th>
                  User ID
                </th>

                <th>
                  Remarks
                </th>

                <th>
                  Trigger Pool
                </th>

                <th>
                  Paid Pool
                </th>

                <th>
                  Reward
                </th>

                <th>
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {/* =================================================
                  LOADING
              ================================================= */}

              {autoPoolPaymentsLoading && (
                <tr>

                  <td
                    colSpan="8"
                    className="gi-table-message"
                  >

                    <FiLoader
                      className="spin"
                      style={{
                        marginRight: "8px",
                      }}
                    />

                    Loading auto pool payments...

                  </td>

                </tr>
              )}

              {/* =================================================
                  ERROR
              ================================================= */}

              {!autoPoolPaymentsLoading &&
                autoPoolPaymentsError && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >

                      {autoPoolPaymentsError}

                    </td>

                  </tr>

                )}

              {/* =================================================
                  NO DATA
              ================================================= */}

              {!autoPoolPaymentsLoading &&
                !autoPoolPaymentsError &&
                autoPoolPayments.length ===
                0 && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      No auto pool payment
                      data found
                    </td>

                  </tr>

                )}

              {/* =================================================
                  PAYMENT RECORDS
              ================================================= */}

              {!autoPoolPaymentsLoading &&
                !autoPoolPaymentsError &&
                autoPoolPayments.map(
                  (item, index) => {

                    const user =
                      item?.userId || {};

                    return (
                      <tr
                        key={
                          item?._id ||
                          index
                        }
                      >

                        {/* =================
                            SL NO
                        ================= */}

                        <td>
                          {(
                            (currentPage -
                              1) *
                            currentLimit +
                            index +
                            1
                          )}
                        </td>

                        {/* =================
                            USER
                        ================= */}

                        <td>

                          <div>
                            <strong>
                              {user?.name ||
                                "-"}
                            </strong>

                            {user?.email && (
                              <div
                                style={{
                                  fontSize:
                                    "12px",
                                  opacity:
                                    0.7,
                                }}
                              >
                                {user.email}
                              </div>
                            )}

                          </div>

                        </td>

                        {/* =================
                            USER ID
                        ================= */}

                        <td>
                          {user?.userId ||
                            "-"}
                        </td>

                        {/* =================
                            REMARKS
                        ================= */}

                        <td>

                          <span
                            className={`gi-status-badge ${getStatusClass(
                              item?.remarks
                            )}`}
                          >

                            {item?.remarks ||
                              "-"}

                          </span>

                        </td>

                        {/* =================
                            TRIGGER POOL
                        ================= */}

                        <td>

                          <strong>
                            Pool{" "}
                            {item?.triggerPool ||
                              "-"}
                          </strong>

                        </td>

                        {/* =================
                            PAID POOL
                        ================= */}

                        <td>

                          <strong>
                            Pool{" "}
                            {item?.paidPool ||
                              "-"}
                          </strong>

                        </td>

                        {/* =================
                            REWARD
                        ================= */}

                        <td>

                          <strong>
                            {formatReward(
                              item?.reward
                            )}
                          </strong>

                        </td>

                        {/* =================
                            DATE
                        ================= */}

                        <td>
                          {formatDate(
                            item?.createdAt
                          )}
                        </td>

                      </tr>
                    );
                  }
                )}

            </tbody>

          </table>

        </div>

        {!autoPoolPaymentsLoading &&
          !autoPoolPaymentsError &&
          totalPages > 0 && (

            <div className="gi-users-pagination">

              {/* ================= PAGINATION INFO ================= */}

              <div className="gi-pagination-info">

                Showing{" "}

                <strong>
                  {totalRecords > 0
                    ? (page - 1) * limit + 1
                    : 0}
                </strong>

                {" "}to{" "}

                <strong>
                  {Math.min(
                    page * limit,
                    totalRecords
                  )}
                </strong>

                {" "}of{" "}

                <strong>
                  {totalRecords}
                </strong>

                {" "}payments

              </div>


              {/* ================= PAGINATION BUTTONS ================= */}

              <div className="gi-pagination-buttons">

                {/* PREVIOUS */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handlePrevious}
                  disabled={!hasPreviousPage}
                >

                  <FiChevronLeft />

                  <span>
                    Prev
                  </span>

                </button>


                {/* PAGE NUMBERS */}

                {getPageNumbers().map(
                  (pageNumber, index) => {

                    if (pageNumber === "...") {

                      return (
                        <span
                          key={`dots-${index}`}
                          className="gi-pagination-dots"
                        >
                          ...
                        </span>
                      );

                    }

                    return (
                      <button
                        type="button"
                        key={pageNumber}
                        className={`gi-pagination-number ${page === pageNumber
                          ? "active"
                          : ""
                          }`}
                        onClick={() =>
                          handlePageChange(
                            pageNumber
                          )
                        }
                      >
                        {pageNumber}
                      </button>
                    );

                  }
                )}


                {/* NEXT */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handleNext}
                  disabled={!hasNextPage}
                >

                  <span>
                    Next
                  </span>

                  <FiChevronRight />

                </button>

              </div>

            </div>

          )}

      </section>

    </div>
  );
};

export default AutoPoolPayment;